// DIVIDIR EN CAPAS — política + cliente de la herramienta del editor Canva.
//
// Parte la capa de imagen seleccionada en 3 capas RGBA apilables (fal
// qwen-image-layered vía nuestro backend): el fondo por un lado y los elementos por
// otro, cada uno movible por separado. Cuesta 1 crédito.
//
// La fuente NO viaja como `imagen_id`: la capa del lienzo puede venir de una subida,
// de `brand_assets` o de la imagen base, así que se sube el PNG al bucket bajo el
// prefijo de la empresa y al backend le viaja la key RELATIVA — mismo patrón que la
// máscara de `edit_preciso` (ADR-634). Las capas resultantes viven SOLO en Storage:
// no son filas del historial. Ver ADR-2026-08-17-dividir-en-capas.

import { USE_ATLAS_BACKEND } from '../../../config';
import { atlasBackendFetch } from '../../../lib/atlasBackend';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { supabase } from '../../../lib/supabase';
import type { ImageLayer } from '../scene/types';
import { loadImageRatio, uid } from './factory';

/** En cuántas capas se parte. Espejo de `CAPAS_POR_DIVISION` del backend. */
export const CAPAS_POR_DIVISION = 3;

/** Lo que cuesta dividir. Espejo del débito del backend (1 por entrega). */
export const CREDITOS_DIVISION = 1;

/**
 * La herramienta se ofrece SOLO contra el backend nuevo: `image_layers` no existe en
 * el n8n viejo, así que con el flag apagado el botón no tendría a quién pegarle.
 */
export const DIVISION_DISPONIBLE = USE_ATLAS_BACKEND;

const MENSAJE_SIN_CREDITOS =
  'No te alcanzan los créditos para dividir la imagen. Sumá créditos o actualizá tu plan para continuar.';

const MENSAJE_ERROR = 'No pudimos dividir la imagen en capas. Intentá de nuevo.';

export interface CapaResultante {
  src: string;
  naturalRatio: number;
}

/**
 * Arma las capas de la escena a partir del resultado.
 *
 * Heredan caja, rotación y esquinas de la original para que, apenas termina la
 * división, el lienzo se vea EXACTAMENTE igual que antes: recién cuando el usuario
 * arrastra una capa se nota que la foto se partió. El orden es el que devolvió el
 * modelo, de abajo hacia arriba (índice 0 = la de más atrás).
 */
export function capasDesdeOriginal(
  original: ImageLayer,
  capas: CapaResultante[],
): ImageLayer[] {
  return capas.map((capa) => ({
    ...original,
    id: uid(),
    src: capa.src,
    // El ratio real de CADA PNG: fal puede devolver las capas en otra resolución que
    // la fuente, y de ese número depende que el resize salga proporcional.
    naturalRatio: capa.naturalRatio,
    // El recorte (si algún día existe en la UI) ya vino aplicado en la fuente que se
    // mandó a dividir: heredarlo lo aplicaría dos veces.
    crop: undefined,
  }));
}

/**
 * Las URLs firmadas EN EL ORDEN de los paths que devolvió el backend.
 *
 * De este orden sale el z-order de la escena, y `createSignedUrls` no promete
 * devolver las firmas en el mismo orden en que se las pidió: si se apilan al revés,
 * el fondo tapa todo lo demás y la división se ve como si no hubiera pasado nada.
 * Las que no se pudieron firmar se caen (mejor 2 capas que ninguna).
 */
export function ordenarCapas(
  paths: string[],
  firmadas: Array<{ path?: string | null; signedUrl?: string | null }>,
): string[] {
  const porPath = new Map(firmadas.map((f) => [f.path ?? '', f.signedUrl ?? null]));
  return paths.map((p) => porPath.get(p) ?? null).filter((u): u is string => !!u);
}

/** Extensión del archivo fuente según lo que devolvió el bucket. */
function extensionDe(mime: string): string {
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
  if (mime === 'image/webp') return 'webp';
  return 'png';
}

/**
 * Divide la capa y devuelve las capas nuevas listas para meter en la escena.
 *
 * Tira `GenerateAdError` de tipo billing en 402 para que la UI abra el modal de saldo
 * de siempre, y `Error` con mensaje en castellano para el resto.
 */
export async function dividirEnCapas(params: {
  layer: ImageLayer;
  companyId: string;
  /** Etiqueta de analytics de la superficie desde la que se dividió. */
  modoUtilizado?: string;
}): Promise<ImageLayer[]> {
  // 1. La imagen de la capa, tal cual la ve el lienzo. Se manda el archivo ENTERO
  //    (no un rasterizado de la caja): así las capas vuelven con el mismo encuadre
  //    natural y entran en la caja de la original sin recortarse.
  let blob: Blob;
  try {
    const res = await fetch(params.layer.src);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    blob = await res.blob();
  } catch (e) {
    console.error('[dividirEnCapas] no se pudo leer la imagen de la capa', e);
    throw new Error('No pudimos leer la imagen de esa capa. Probá con otra.');
  }

  // 2. Al bucket, bajo el prefijo de la empresa (RLS permite escribir ahí).
  const sourceKey = `capas-src/${uid()}.${extensionDe(blob.type)}`;
  const { error: upErr } = await supabase.storage
    .from('generadas')
    .upload(`${params.companyId}/${sourceKey}`, blob, {
      contentType: blob.type || 'image/png',
      upsert: false,
    });
  if (upErr) {
    console.error('[dividirEnCapas] upload de la fuente falló', upErr);
    throw new Error('No pudimos subir la imagen para dividirla. Intentá de nuevo.');
  }

  // 3. El backend: llama a fal, sube las capas y cobra el crédito al entregar.
  const res = await atlasBackendFetch('/v1/images/layers', {
    source_key: sourceKey,
    modo_utilizado: params.modoUtilizado ?? 'dividir_capas_canva',
  });
  if (res.status === 402) {
    throw new GenerateAdError('billing', MENSAJE_SIN_CREDITOS, 'billing');
  }
  const data = (await res.json().catch(() => null)) as {
    response?: string;
    layer_paths?: string[];
    error?: string;
  } | null;
  if (!res.ok || data?.response !== 'success' || !data.layer_paths?.length) {
    throw new Error(data?.error || MENSAJE_ERROR);
  }

  // 4. Firmar las capas (vienen como paths del bucket) y leerles el ratio real.
  const { data: firmadas } = await supabase.storage
    .from('generadas')
    .createSignedUrls(data.layer_paths, 3600);
  const urls = ordenarCapas(data.layer_paths, firmadas ?? []);
  if (urls.length === 0) {
    throw new Error('Las capas se generaron pero no pudimos cargarlas. Intentá de nuevo.');
  }
  const resultado = await Promise.all(
    urls.map(async (src) => ({ src, naturalRatio: await loadImageRatio(src) })),
  );
  return capasDesdeOriginal(params.layer, resultado);
}
